import { useMemo, useState } from 'react'
import dayjs from 'dayjs'

import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import { assessmentTypeToLabel } from '@shared/utils'

import AssessmentDetailsModal from '@pages/Assessments/AssessmentDetailsModal'
import StatusChip from '@components/StatusChip'
import { Item } from '@components/TableCell'
import Provider from '@components/TableCell/Provider'

export default function AssessmentsForReview({ provider, assessments, open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      sx={{ '& .MuiDialog-container': { justifyContent: 'flex-end' } }}
    >
      <Content provider={provider} assessments={assessments} onClose={onClose} />
    </Dialog>
  )
}

function Content({ provider, assessments = [], onClose }) {
  const [isAsmtDetailsOpen, setIsAsmtDetailsOpen] = useState(false)
  const [selectedAsmtId, setSelectedAsmtId] = useState(null)

  const sortedAssessments = useMemo(() => {
    return [...assessments].sort((a, b) => {
      if (a.flagged !== b.flagged) return b.flagged - a.flagged
      return new Date(a.dateReturned) - new Date(b.dateReturned)
    })
  }, [assessments])

  return (
    <>
      <AssessmentDetailsModal
        canMarkExceptional
        align="flex-start"
        open={isAsmtDetailsOpen}
        assessmentId={selectedAsmtId}
        onClose={() => setIsAsmtDetailsOpen(false)}
      />
      <DialogTitle onClose={onClose}>
        <Provider user={provider} />
      </DialogTitle>
      <DialogContent>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell align="center">Async Age</TableCell>
                <TableCell align="right">Last Lab</TableCell>
                <TableCell align="right">Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAssessments.map((assessment) => (
                <AssessmentRow
                  key={assessment.id}
                  data={assessment}
                  onClick={() => {
                    setSelectedAsmtId(assessment.id)
                    setIsAsmtDetailsOpen(true)
                  }}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </>
  )
}

function AssessmentRow({ data: assessment, onClick }) {
  const asyncAgeError = assessment.asyncAge <= 60

  return (
    <TableRow
      hover
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        borderLeft: (theme) => `${theme.spacing(0.5)} solid ${assessment.flagged ? theme.palette.error.main : 'transparent'}`,
      }}
    >
      <TableCell>
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
          <Item small primary={assessmentTypeToLabel[assessment.assessmentType]} secondary={assessment.id} />
          {assessment.medicaidPatient && <Chip size="small" label="Medicaid" color="info" />}
        </Stack>
      </TableCell>
      <TableCell align="center" sx={{ py: 0.5 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: asyncAgeError ? 'bold' : undefined,
            color: asyncAgeError ? 'error.main' : undefined,
          }}
        >
          {assessment.asyncAge !== undefined ? assessment.asyncAge : '—'}
        </Typography>
        {assessment.flagged && <StatusChip icon label="Flagged" type="errorFilled" />}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">
          {assessment.lastCompletedLabDate ? dayjs(assessment.lastCompletedLabDate).format('L') : '—'}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{assessment.dateReturned ? dayjs(assessment.dateReturned).format('L') : '—'}</Typography>
      </TableCell>
    </TableRow>
  )
}
